import { getDocumentBaseHref, normalizeBaseHref } from '@app/core/services/app-route-prefix.service';

export type Theme = {
    themeName: string;
    css: string;
    default?: boolean;

    // These are just for display in the theme switcher. They are not synced with the actual theme.
    backgroundColor?: string;
    color?: string;
    fontFamily?: string;
};

export const DEFAULT_THEME_NAME = 'PHMS';
export const DEFAULT_THEME_CSS = 'phms.theme.styles.css';

export const Themes: Theme[] = [
    { themeName: DEFAULT_THEME_NAME, css: DEFAULT_THEME_CSS, default: true, backgroundColor: '#174479', color: 'white', fontFamily: 'IBM Plex Sans' },
    { themeName: 'Horizon', css: 'horizon.theme.styles.css', backgroundColor: '#173d63', color: 'white', fontFamily: 'IBM Plex Sans' },
    { themeName: 'Graphite', css: 'graphite.theme.styles.css', backgroundColor: '#303842', color: 'white', fontFamily: 'IBM Plex Sans' },
    { themeName: 'Indigo & Seafoam', css: 'indigo-seafoam.theme.styles.css', backgroundColor: '#4338ca', color: 'white', fontFamily: 'IBM Plex Sans' },
    { themeName: 'Aubergine & Copper', css: 'aubergine-copper.theme.styles.css', backgroundColor: '#593659', color: 'white', fontFamily: 'IBM Plex Sans' },
    { themeName: 'Evergreen & Denim', css: 'evergreen-denim.theme.styles.css', backgroundColor: '#245347', color: 'white', fontFamily: 'IBM Plex Sans' },
    { themeName: 'Template', css: 'template.theme.styles.css', backgroundColor: '#174479', color: 'white', fontFamily: 'Segoe UI' },
    { themeName: 'CAPS', css: 'caps.theme.styles.css', backgroundColor: '#99e1e3', color: 'black', fontFamily: 'Segoe UI' },
    { themeName: 'AltFont', css: 'altfont.theme.styles.css', backgroundColor: 'green', color: 'yellow', fontFamily: "'Ink Free Regular', Papyrus" },
    { themeName: 'No', css: 'no.theme.styles.css', backgroundColor: '#f5f5f5', color: '#333', fontFamily: 'Arial' }
];

// Selectors can have spaces. Class names cannot.
export const ThemeVariants = [
    { name: 'Light', globalSelector: 'html', globalClassName: 'light-mode', agGridSelector: '.ag-grid-container', agGridClassName: 'ag-theme-material' },
    { name: 'Dark', globalSelector: 'html', globalClassName: 'dark-mode', agGridSelector: '.ag-grid-container', agGridClassName: 'ag-theme-material-dark' }
];

const LEGACY_DEFAULT_THEME_NAMES = new Set(['bits', 'jms']);

export function normalizeThemeName(themeName?: string | null): string {
    const requestedThemeName = themeName?.trim();

    if (!requestedThemeName) {
        return DEFAULT_THEME_NAME;
    }

    const lowerThemeName = requestedThemeName.toLowerCase();
    if (LEGACY_DEFAULT_THEME_NAMES.has(lowerThemeName)) {
        return DEFAULT_THEME_NAME;
    }

    const configuredTheme = Themes.find((theme) => theme.themeName.toLowerCase() === lowerThemeName);
    return configuredTheme?.themeName ?? requestedThemeName;
}

export function resolveTheme(themeName?: string | null): Theme | undefined {
    const normalizedThemeName = normalizeThemeName(themeName);

    return Themes.find((theme) => theme.themeName.toLowerCase() === normalizedThemeName.toLowerCase());
}

/* eslint-disable no-restricted-syntax */

/**
 * ThemeSwitcher
 * Once initialized, this class will watch for changes in the URL and change the theme based on the URL parameter 'theme:<theme name>'.
 * It operates by changing the href of the link element in the head of the document.
 * The document is the preview iframe in Storybook... which means that it's not operating
 * on the URL that you see in the browser, but the URL that the preview iframe is using.
 */
export class ThemeSwitcher {
    // Create a singleton instance - This is NOT a service
    private static instance: ThemeSwitcher;

    // The current theme CSS file.
    currentCssName?: string;
    currentThemeVariantName?: string = 'Light';

    globalVariableName_app_theme: string = 'app_theme';
    globalVariableName_theme_variant: string = 'theme_variant';

    isStorybook: boolean = false;

    _useSystemTheme: boolean = true;
    get useSystemTheme(): boolean {
        return this._useSystemTheme;
    }
    set useSystemTheme(value: boolean) {
        this._useSystemTheme = value;
        this.systemDarkModeChanged();
    }

    private constructor() {

        /* Watch for system dark mode changes
           This is for the application AND for Storybook.
        */
        window
            .matchMedia('(prefers-color-scheme: dark)')
            .addEventListener('change', (mediaQuery) => {
                this.systemDarkModeChanged();
            });

        // Initial check for system dark mode
        this.systemDarkModeChanged();
    }

    /**
     * Singleton instance - Can be used in the application by the ThemeManagerService or Storybook
     * @returns ThemeSwitcher
     */
    public static getInstance(): ThemeSwitcher {
        if (!ThemeSwitcher.instance) {
            ThemeSwitcher.instance = new ThemeSwitcher();
        }

        return ThemeSwitcher.instance;
    }

    // The globals from the URL in Storybook
    globals: Map<string, string> = new Map<string, string>();

    /**
     * Storybook only
     * Get the globals from the URL. This is for Storybook only but does not use
     * the Storybook API. It's a workaround because the Storybook API is not available
     */
    getGlobals() {
        // This only works in decorators?
        //const [globals, updateGlobals] = useGlobals();
        //this.globals = globals;

        const urlParams = new URLSearchParams(window.location.search);

        // Get the globals parameter from the URL
        const globals = urlParams.get('globals');

        if (!globals) {
            //console.log('globals not found in URL, using default theme.');
        }
        else {
            const arrGlobals = globals.split(';');

            arrGlobals.forEach((item) => {
                const keyValuePair = item.split(':');
                if (keyValuePair.length === 2) {
                    this.globals.set(keyValuePair[0], keyValuePair[1]);
                }
            });
        }
    }

    // watchForChange: boolean = false;
    static isInitialized: boolean = false;

    /**
     * Storybook only - The application should not call this method because it doesn't use the URL to know when to change themes (although it could if we wanted it to)
     * This is called by Storybook or the application to initialize the ThemeSwitcher
     * It sets up the event listener for the URL change event.
     */
    public static initialize() {

        const themeSwitcher = ThemeSwitcher.getInstance();

        // Only Storybook calls initialize
        themeSwitcher.isStorybook = true;


        //g('ThemeSwitcher initialized. From now on, the theme will change automatically based on the URL parameter theme:<theme name>');



        // This is the firefox-friendly way to detect a change in the URL
        if (!(window as any).navigation) {
            console.warn('window.navigation is undefined (using Firefox?). Theme switching will not work.');

            // Tried to use popstate but couldn't get it to work. Good luck.
        }
        else {
            // Detect a change in the URL - dosn't work in Firefox because window.navigation is undefined
            // TODO: change to document.addEventListener('navigate', (event) => { ... });
            // document.addEventListener doesn't have a 'navigate' event. so....
            // TODO: If this gets moved to the application, then use the Angular ActivatedRoute to detect a change in the URL
            (window as any).navigation.addEventListener("navigate", (event: any) => {
                themeSwitcher.onUrlChange(event);
            });
        }

        if (!ThemeSwitcher.isInitialized) {
            // themeSwitcher.setDefaultTheme();
            ThemeSwitcher.isInitialized = true;
        }
    }

    /**
     * This is for Storybook only. It detects changes to the query parameters in the URL
     * and changes the theme and variant based on the 'theme' and 'theme_variant' parameter.
     * @param event
     */
    onUrlChange(event: any) {
        //console.log('Storybook location changed! Might change theme');
        //console.log('event=' + event);

        // Wait a bit before changing the theme, otherwise it uses the old value of the location
        setTimeout(() => {
            //console.log('window.location = ' + window.location);
            this.getGlobals();
            this.changeThemeFromUrl();
            this.changeThemeVariantFromUrl();
        }, 250);
    }

    // setDefaultTheme()
    // {
    //     // let defaultTheme = Themes.find((t) => t.default);
    //     // if(!defaultTheme)
    //     // {
    //     //     console.log('Default theme not found.');
    //     //     //this.loadStyle('bits');
    //     //     return;
    //     // }
    //     // this.loadStyle(defaultTheme.css);

    //     console.log('setDefaultTheme');
    //     if ('URLSearchParams' in window) {
    //         let searchParams = new URLSearchParams(window.location.search);
    //         if(!searchParams.toString().includes(`${this.globalVariableName_app_theme}:`))
    //         {
    //             searchParams.set('globals', `${this.globalVariableName_app_theme}:${DEFAULT_THEME_NAME}`);
    //             let searchParamsString = searchParams.toString();
    //             console.log('searchParamsString=' + searchParamsString);

    //             // Only set the window.location.search if it's not already set

    //             let decodeeURI = window.decodeURI((window as any).location);
    //             //debugger
    //             if(!decodeeURI.includes(`${this.globalVariableName_app_theme}:${DEFAULT_THEME_NAME}`))
    //             {
    //                 //window.location.search = searchParamsString;
    //             }
    //             //window.location.search = searchParams.toString();
    //         }


    //         // Set the URL parameter 'globals' to the default theme if it's not already set without causing a page relaod.
    //         // But this doesn't set the URL in the browser, so it's not useful.
    //         if ('URLSearchParams' in window)
    //         {
    //             const url = new URL((window as any).location)
    //             /*
    //               these both give you something like this: http://localhost:6006/iframe.html?viewMode=docs&id=material-inputs--docs
    //              */
    //             console.log(`searchParams=${searchParams}`);  // From the iFrame
    //             console.log(`document.location=${document.location}`);  // From the iFrame
    //             console.log(`window.location=${window.location}`);   // From the iFrame

    //             url.searchParams.set('globals', `${this.globalVariableName_app_theme}:${DEFAULT_THEME_NAME}`);
    //             //history.pushState(null, '', url);
    //         }
    //     }
    // }


    /**
     * Storybook only.
     * Change the theme based on the URL parameter 'theme:<theme name>'
     * this.globals should already be set.
     */
    changeThemeFromUrl() {
        let css: string = Themes.find((t) => t.default)?.css || DEFAULT_THEME_CSS;

        console.log(`Changing theme to ${css}`);

        const urlParams = new URLSearchParams(window.location.search);

        const themeName = this.globals.get(this.globalVariableName_app_theme);
        if (!themeName) {
            console.log('theme not found in globals, loading default');
        }
        else {
            const theme = resolveTheme(themeName);
            if (!theme) {
                const styleName = themeName.toLocaleLowerCase().trim();
                css = `${styleName}.theme.styles.css`;

                console.warn(`Theme not found in Themes array, using old method of using theme name "${styleName}" and assuming the css filename is ${css}. The Themes array should be updated to include this theme.`);

            }
            else {
                console.log(`Found theme ${theme.themeName} in themes array. Setting stylesheet to ${theme.css}`);
                css = theme.css;
            }
        }

        if (this.currentCssName != css) {
            this.loadThemeStyle(css);
        }

    }

    /**
     * Storybook only.
     * Change the theme variant based on the URL parameter 'theme_variant:<variant>'
     * Note that we need to change the head element as well as all agGrid parent elements.
     */
    changeThemeVariantFromUrl() {
        let variantName: string = 'Light';

        if (this.globals.has(this.globalVariableName_theme_variant)) {
            variantName = this.globals.get(this.globalVariableName_theme_variant) || 'Light';

            if (variantName == 'System') {
                this.useSystemTheme = true;
            }
            else {
                this.useSystemTheme = false;
                this.setThemeVariantByName(variantName as "Light" | "Dark");
            }
        }
    }

    setThemeVariantByName(variantName: "Light" | "Dark") {
        const currentThemeVariant = ThemeVariants.find((t) => t.name === this.currentThemeVariantName);
        let themeVariant = ThemeVariants.find((t) => t.name === variantName);
        if (!themeVariant) {
            //console.log('Theme variant not found in ThemeVariants array. Using default theme variant.');
            themeVariant = ThemeVariants.find((t) => t.name === 'Light');
        }
        // else
        // {
        //     console.log(`Setting theme variant to ${themeVariant.name}`);
        // }


        if (themeVariant && currentThemeVariant && themeVariant.name != currentThemeVariant.name) {
            // Swap the global classes
            this.swapClasses(themeVariant.globalSelector, currentThemeVariant.globalClassName, themeVariant.globalClassName);

            // Swap the agGrid classes
            this.swapClasses(themeVariant.agGridSelector, currentThemeVariant.agGridClassName, themeVariant.agGridClassName);

            // Set the new theme variant
            this.currentThemeVariantName = themeVariant.name;
        }
    }


    /**
     * Swap classes on elements that match the selector
     * @param selector The CSS selector to match elements
     * @param oldClass The class to remove
     * @param newClass The class to add
     * @example
     * swapClasses('.ag-grid-container', 'ag-theme-material', 'ag-theme-material-dark');
     * swapClasses('.ag-grid-container', 'ag-theme-material-dark', 'ag-theme-material');
     * swapClasses('html', 'light-theme', 'dark-theme');
     */
    swapClasses(selector: string, oldClass: string, newClass: string) {
        if (document) {
            const elements = document.querySelectorAll(selector);

            elements.forEach((element) => {
                element.classList.remove(oldClass);
                element.classList.add(newClass);
            });
        }
    }


    /**
     * Load a theme style sheet by modifying the href of a link element in the head.
     * @param cssName  - The name of the theme to load
     */
    loadThemeStyle(cssName: string) {
        const head = document.getElementsByTagName("head")[0];

        const styleLinkElementId = `theme-link`;

        const rando = Math.floor(Math.random() * 1000);

        const appBaseHref = getDocumentBaseHref();
        const url = window.location.href;
        let root = "";

        /* Why is this section necessary?
           Because the Storybook builder doesn't let you specify style bundles like the Angular builder does.
           So, there's no way (that I know of) to have the Storybook builder create or copy in the theme css
           files to be available in the Storybook folder (without running some kind of command AFTER the build).
           So, we assume the application base href points to the app that has the theme files.
           Note that if you're running Storybook locally on port 6006, then you'll need to also be running
           the app locally on port 4200.
         *
         */
        if (this.isStorybook) {
            root = url.substring(0, url.lastIndexOf('/'));  // Get's the root without the last slash

            //console.log(`Trying to infer root from ${root}`);

            if (root.includes('localhost:6006')) {

                // Don't do anything, now copying theme files to storybook folder
                console.warn(`Switching themes in Storybook on localhost:6006 will only work if the app is running on localhost:4200`);
                root = joinRootAndBaseHref('http://localhost:4200', appBaseHref);
            }
            else if (root.endsWith('localhost')) {
                root = joinRootAndBaseHref(root, appBaseHref);
            }
            else {
                /* Otherwise, assume there's a /storybook path, this should work for localhost IIS as well as our regular environments */
                root = root.replace('/storybook', normalizeBaseHref(appBaseHref));
            }
        }

        const href = `${root}${cssName}?r=${rando}`;
        //console.log(`Looking for ${cssName} theme file here: ${href}`);
        const themeLink = document.getElementById(styleLinkElementId) as HTMLLinkElement;
        if (themeLink) {
            // eslint-disable-next-line no-console
            console.info(`Setting theme to ${href}`);
            themeLink.href = href;
        }
        else {
            console.warn(`Theme link not found, will try to create`);
            // This should never happen if the link tag is already defined in the index.html file.
            const style = document.createElement("link");
            style.type = "text/css";
            style.id = styleLinkElementId;
            style.rel = "stylesheet";
            style.href = href;

            head.appendChild(style);
        }

        // Also, set the class on the html element
        //this.swapClasses(this.materialThemeSelector, this.currentStyleName, themeName);
        this.currentCssName = cssName;

    }

    /**
     * Trigger a theme mode change when the system dark mode changes and the theme mode is set to 'System'.
     */
    systemDarkModeChanged(): void {
        //if (this.currentThemeVariantName === 'System') {
        if (this.useSystemTheme) {
            this.setThemeVariantFromSystem();
        }
    }

    setThemeVariantFromSystem() {
        if (window.matchMedia) {
            if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
                // dark mode
                this.setThemeVariantByName('Dark');
            }

            if (window.matchMedia('(prefers-color-scheme: light)').matches) {
                // light mode
                this.setThemeVariantByName('Light');
            }
        }
    }
}

/* eslint-enable no-restricted-syntax */

function joinRootAndBaseHref(root: string, baseHref: string): string {
    return `${root.replace(/\/+$/, '')}${normalizeBaseHref(baseHref)}`;
}
